import type { MarketGroupsApi } from "@/v2/api/metadata/market-groups";
import type { MarketsApi } from "@/v2/api/metadata/markets";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { DialogConfig } from "@/v2/framework/dialog/types";
import { buildAddSupplementalNamesPayload } from "@/v2/kits/supplemental-names";
import {
  marketGroupGamesStatusOptions,
  marketGroupPositionValidator,
  marketGroupStatusOptions,
} from "../../shared";
import { marketGroupMarketIds, marketGroupTags, marketGroupTypes } from "./collections";

export function createAddDialog(options: {
  marketGroupsApi: MarketGroupsApi;
  sportsApi: SportsApi;
  marketsApi: MarketsApi;
}): DialogConfig {
  const { marketGroupsApi, sportsApi, marketsApi } = options;

  return {
    id: "add-market-group",
    title: "Add market group",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: marketGroupStatusOptions,
                  defaultValue: 1,
                },
                {
                  type: "select-field",
                  name: "sportId",
                  label: "Sport",
                  required: true,
                  placeholder: "Select sport",
                  searchable: true,
                  options: async () => {
                    const sports = await sportsApi.getFilters({ page: 1, size: -1 });
                    return sports.map((sport) => ({
                      value: sport.id as string | number,
                      title: String(sport.computedName ?? sport.name ?? ""),
                    }));
                  },
                },
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                { type: "text-field", name: "info", label: "Info", placeholder: "Enter info" },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                  inputType: "number",
                  validate: marketGroupPositionValidator,
                },
                {
                  type: "component",
                  component: () => import("./MarketGroupCollectionsFields.vue"),
                  props: { mode: "add", marketsApi },
                },
                {
                  type: "component",
                  component: () =>
                    import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
                  props: { mode: "add", entity: "MARKET_GROUP" },
                },
              ],
            },
            {
              type: "group",
              name: "prematchGame",
              label: "Pre-match",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: marketGroupGamesStatusOptions,
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  inputType: "number",
                  validate: marketGroupPositionValidator,
                },
              ],
            },
            {
              type: "group",
              name: "liveGame",
              label: "Live",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: marketGroupGamesStatusOptions,
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  inputType: "number",
                  validate: marketGroupPositionValidator,
                },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>;
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>;
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>;
        const marketIds = marketGroupMarketIds(data);
        const types = marketGroupTypes(data);
        if (!marketIds.length || !types.length) {
          throw new Error("Select markets and types");
        }

        return marketGroupsApi.create({
          sportId: main.sportId,
          status: main.status,
          name: main.name,
          info: main.info === "" ? null : main.info,
          position: main.position === "" || main.position == null ? null : Number(main.position),
          marketIds,
          types,
          tags: marketGroupTags(data),
          displayModeType: data.displayModeType ?? 1,
          supplementalNames: buildAddSupplementalNamesPayload(data, "MARKET_GROUP"),
          prematchGame: {
            status: prematchGame.status,
            position:
              prematchGame.position === "" || prematchGame.position == null
                ? null
                : Number(prematchGame.position),
          },
          liveGame: {
            status: liveGame.status,
            position:
              liveGame.position === "" || liveGame.position == null
                ? null
                : Number(liveGame.position),
          },
        });
      },
    },
  };
}
